import { Bell, Calendar, Gauge, ShieldCheck, UserRound } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  AdminPanel,
  CalendarSyncDefaults,
  NotificationsPanel,
  ProfilePanel,
  UsagePanel,
} from '@/components/account';
import { useShellHeader } from '@/components/contexts/ShellHeaderContext';
import Loading from '@/components/shared/Loading';
import { HeaderTabs } from '@/components/ui/HeaderTabs';
import { useSuperAdminCheck } from '@/hooks/useSuperAdminCheck';
import { useAuth } from '@/lib/auth/hooks/auth-provider';
import { fullName } from '@/lib/user';
import { initialsFromName } from '@/lib/utils';

type AccountTab = 'profile' | 'calendar' | 'usage' | 'notifications' | 'admin';

const Account = () => {
  const { user: sessionUser, isLoading, refresh } = useAuth();
  const { isSuperAdmin } = useSuperAdminCheck();
  const user = sessionUser?.dbUser;
  const [tab, setTab] = useState<AccountTab>('profile');

  useShellHeader({
    header: 'Account',
    subheader: 'Your profile, usage, and preferences',
  });

  const tabs = useMemo(
    () => [
      { value: 'profile', label: 'Profile', icon: UserRound },
      { value: 'calendar', label: 'Calendar', icon: Calendar },
      { value: 'usage', label: 'Usage', icon: Gauge },
      { value: 'notifications', label: 'Notifications', icon: Bell },
      ...(isSuperAdmin ? [{ value: 'admin', label: 'Admin', icon: ShieldCheck }] : []),
    ],
    [isSuperAdmin],
  );

  if (isLoading) {
    return <Loading />;
  }

  if (!user) return null;

  return (
    <div className="overflow-x-hidden">
      <div className="mx-auto max-w-5xl px-8 py-8">
        <div className="mb-8 flex items-center gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-crimson-700 text-lg font-medium text-white">
            {initialsFromName(fullName(user))}
          </div>
          <div className="min-w-0">
            <h1 className="truncate text-lg font-semibold tracking-tight text-foreground">
              {fullName(user)}
            </h1>
            <p className="truncate text-sm text-muted-foreground">
              {user.email}
              {user.position ? ` · ${user.position}` : ''}
            </p>
          </div>
        </div>

        <div className="mb-6 border-b border-border">
          <HeaderTabs
            items={tabs}
            value={tab}
            onChange={(value) => setTab(value as AccountTab)}
            ariaLabel="Account sections"
            idBase="account"
          />
        </div>

        <div role="tabpanel" id={`account-panel-${tab}`} aria-labelledby={`account-tab-${tab}`}>
          {tab === 'profile' && <ProfilePanel key={user.guid} user={user} onUserUpdated={refresh} />}
          {tab === 'calendar' && <CalendarSyncDefaults />}
          {tab === 'usage' && <UsagePanel />}
          {tab === 'notifications' && <NotificationsPanel />}
          {tab === 'admin' && isSuperAdmin && <AdminPanel />}
        </div>
      </div>
    </div>
  );
};

export default Account;
